import React from 'react';
import {
  Box,
  Button,
  Divider,
  FormErrorMessage,
  Image,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text
} from '@chakra-ui/react';
import TwygsButton from '../../../../components/Button/index';
import { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { GENDER } from '../../../../constants/index';
import { updateProfile } from '../../../../apis/article.api';
import { useAuthGateway } from '../../hooks/useAuthGateway';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { profileSchema } from '../../../../components/Settings/schema.js';
import { useAtom } from 'jotai';
import CalendarIcon from '../../../../assets/calendar-icon.svg';
import CalendarDisableIcon from '../../../../assets/calendar-disable-icon.svg';
import CustomModal from '../../../../components/Modal/CustomModal/index.jsx';
import DoneCircledIcon from '../../../../assets/done-circle-icon.svg';
import DataLayer from '../../../../utils/data-layer';
import EmailIcon from '../../../../assets/email-icon.svg';
import EmailDisableIcon from '../../../../assets/email-disable-icon.svg';
import ArrowDownIconSolid from '../../../../assets/arrow-down-icon.svg';
import { profileAtom } from '../../../../components/Settings/atom/index';
import IntroSection from '../../../../components/Profile/intro-section/intro-section.component.jsx';
import { InputHookForm } from '../../../../components/Common/Form/input-with-hook-form/index.jsx';
import {
  PROFILE_ERROR_MESSAGE,
  PROFILE_MODAL,
  PROFILE_TYPE_MODAL
} from '../../../../components/Profile/constants.js';
import VerifyOtpModal from '../../../../components/Profile/modal/verify-otp';
import VerifyPasswordModal from '../../../../components/Profile/modal/verify-password';
import { authGatewayApi } from '../../../../apis/auth-gateway.api';
import ArrowUpLightGreen from '../../../../assets/arrow-up-light-green-icon.svg';
import WarningCircleIcon from '../../../../assets/warning-circle-icon.svg';
import VerificationPendingModal from './modal/verification-pending';

interface ChangeInfoProps {
  profileData?: any;
  setProfileData?: any;
}

export const GeneralSettings: React.FC<ChangeInfoProps> = ({ profileData, setProfileData }) => {
  const [modal, setModal] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [, setIsMenuOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [verifyPassword, setVerifyPassword] = useState<string>('');

  const [, setProfile] = useAtom(profileAtom);

  const methods = useForm({
    resolver: yupResolver(profileSchema),
    mode: 'onChange'
  });

  const { errors }: { errors: any } = methods.formState;

  const [authGatewayPayload, setAuthGatewayPayload] = useState({});

  const {
    data: authGatewayData,
    isLoading: isLoadingAuthGateway,
    refetch: refetchAuthGateway
  } = useAuthGateway(authGatewayPayload);
  const recoveryEmail = useWatch({ name: 'recoveryEmail', control: methods.control });

  const gender = useWatch({
    name: 'gender',
    control: methods.control,
    defaultValue: GENDER[0]
  });

  const handleSelectGender = (value) => {
    setIsMenuOpen(false);
    methods.setValue('gender', value);
  };

  const handleVerifyRecoveryEmail = async () => {
    await methods.trigger('recoveryEmail');
    if (!methods.formState?.errors?.recoveryEmail) {
      setModal(PROFILE_TYPE_MODAL.VERIFY_PASSWORD);
    }
  };

  const onSubmit = async (data) => {
    try {
      if (!profileData?.recoveryEmail && recoveryEmail) {
        setModal(PROFILE_TYPE_MODAL.PENDING_RECOVERY_EMAIL);
        return;
      }

      let payload = {
        firstName: data.firstName,
        lastName: data.lastName,
        birthday: data.birthday,
        gender: gender.value,
        username: data.username
      };

      setIsLoading(true);
      const res = await updateProfile(payload);

      if (res.statusCode === 400) {
        switch (res.message) {
          case 'Username has already been taken': {
            methods.setError('username', res.message);
            methods.formState.errors.username.message = res.message;
            break;
          }
        }
      } else {
        const newData = {
          ...res
        };

        // Track profile update event
        DataLayer.trackUpdateProfile();

        setModal('success-modal');
        setProfileData(newData);
        setProfile(newData);
      }
    } catch (error) {
      setModal('error-modal');
    } finally {
      setIsLoading(false);
    }
  };

  const renderModal = () => {
    switch (modal) {
      case 'success-modal':
        return (
          <CustomModal
            isOpen
            title="Profile update completed!"
            onClick={() => setModal('')}
            hideFooter
            maxW="407px"
            p={'20px 15px'}
          >
            <div className="w-full flex flex-col items-center">
              <img src={DoneCircledIcon} alt="" />
              <p className="text-lg font-[600] mt-4 text-center px-6">
                Profile updated successfully!
              </p>
              <div className="flex gap-[10px] mt-6 w-full">
                <TwygsButton
                  variant="primary"
                  className="h-[42px] w-full !text-lg"
                  onClick={() => setModal('')}
                >
                  Done
                </TwygsButton>
              </div>
            </div>
          </CustomModal>
        );
      case PROFILE_TYPE_MODAL.VERIFY_PASSWORD:
        return (
          <VerifyPasswordModal
            isOpen
            title={PROFILE_MODAL.VERIFY_PASSWORD.TITLE}
            desc={PROFILE_MODAL.VERIFY_PASSWORD.DESC}
            inputLabel={PROFILE_MODAL.VERIFY_PASSWORD.LABEL}
            placeholder={PROFILE_MODAL.VERIFY_PASSWORD.PLACEHOLDER}
            btnText={PROFILE_MODAL.VERIFY_PASSWORD.CONFIRM}
            errorMessage={errorMessage}
            setErrorMessage={setErrorMessage}
            onClick={handleVerifyPassword}
            onClose={handleCancelModal}
            isLoading={isLoadingAuthGateway}
          />
        );
      case PROFILE_TYPE_MODAL.VERIFY_OTP:
        return (
          <VerifyOtpModal
            isOpen
            title={PROFILE_MODAL.VERIFY_RECOVERY_EMAIL.TITLE}
            desc={PROFILE_MODAL.VERIFY_RECOVERY_EMAIL.DESC}
            errorMessage={errorMessage}
            setErrorMessage={setErrorMessage}
            btnText={PROFILE_MODAL.VERIFY_RECOVERY_EMAIL.CONFIRM}
            onClick={handleVerifyOtp}
            onClose={handleCancelModal}
            handleResendOpt={handleResendOpt}
            isLoading={isLoadingAuthGateway}
          />
        );
      case PROFILE_TYPE_MODAL.VERIFY_SUCCESS:
        return (
          <CustomModal
            isOpen
            title="Verification completed!"
            onClick={() => setModal('')}
            hideFooter
            maxW="407px"
            p={'32px'}
          >
            <div className="w-full flex flex-col items-center">
              <img src={DoneCircledIcon} alt="" />
              <p className="text-lg font-[600] mt-4 text-center px-6">
                Your request has been successfully updated!
              </p>
              <div className="flex gap-[10px] mt-6 w-full">
                <TwygsButton
                  variant="primary"
                  className="!text-white hover:opacity-80 px-5 py-[10px] flex items-center justify-center rounded-[8px]  w-full text-lg font-[900]"
                  onClick={() => setModal('')}
                  h={'42px'}
                >
                  Done
                </TwygsButton>
              </div>
            </div>
          </CustomModal>
        );
      case PROFILE_TYPE_MODAL.PENDING_RECOVERY_EMAIL:
        return (
          <VerificationPendingModal
            isOpen
            Icon={WarningCircleIcon}
            titleBtn={PROFILE_MODAL.PENDING_RECOVERY_EMAIL.CONFIRM}
            title={PROFILE_MODAL.PENDING_RECOVERY_EMAIL.TITLE}
            desc={PROFILE_MODAL.PENDING_RECOVERY_EMAIL.DESC}
            onClose={handleCancelModal}
            onClick={handleVerifyRecoveryEmail}
          />
        );
      default:
        return <></>;
    }
  };

  const handleVerifyOtp = async (value: string) => {
    try {
      const payload = {
        recoveryEmail: methods.getValues('recoveryEmail'),
        verificationCode: value
      };

      // const { data }: any = await mutationVerifyOtpRecoveryEmail.mutateAsync(payload);
      const response: any = await authGatewayApi.verifyOtp(payload);
      if (response?.success) {
        setProfileData({
          ...profileData,
          recoveryEmail: payload.recoveryEmail
        });
        setProfile({
          ...profileData,
          recoveryEmail: payload.recoveryEmail
        });
        setModal(PROFILE_TYPE_MODAL.VERIFY_SUCCESS);
      }
    } catch (e: any) {
      setErrorMessage(PROFILE_ERROR_MESSAGE.OTP);
    }
  };

  const handleResendOpt = () => {
    handleVerifyPassword(verifyPassword);
  };

  const handleVerifyPassword = async (value: string) => {
    try {
      const payload = {
        recoveryEmail: methods.getValues('recoveryEmail'),
        password: value
        // query: 'verify-password'
      };
      const response: any = await authGatewayApi.verifyPassword(payload);
      if (response?.success) {
        setVerifyPassword(value);
        setModal(PROFILE_TYPE_MODAL.VERIFY_OTP);
      }
    } catch (e: any) {
      setErrorMessage(e?.response?.data?.message || PROFILE_ERROR_MESSAGE.SOMETHING);
    }
  };

  const handleCancelModal = () => {
    setModal('');
    setErrorMessage('');
  };

  useEffect(() => {
    if (profileData) {
      const gender = GENDER.find((item) => item.value === profileData?.gender) || GENDER[0];

      methods.reset({
        ...profileData,
        gender
      });
    }
  }, [methods, profileData]);

  return (
    <Box>
      <IntroSection
        title="General"
        description="Edit your basic account details like name, email, username, and more."
        titleSize="text-2xl"
        desSize="text-base"
      />
      <Divider my="1.25rem" color="anti-flash-white" opacity={1} />
      <Box mt={{ base: '1.5rem', md: '0' }} maxW={'100%'}>
        <Box
          display="flex"
          alignItems={{ base: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          maxW={'100%'}
          flexDirection={{ base: 'column', md: 'row' }}
          gap={'0.5rem'}
        >
          <Text fontWeight={600} color="jet-black" w={{ base: '100%', md: '35%' }} fontSize={'sm'}>
            Public profile information
          </Text>
          <Box w={{ base: '100%', md: '65%' }} display="flex" flexWrap={'wrap'}>
            <Text display={'inline-block'} className="text-sm font-[600] mr-5">
              Edit your full name, photo, bio and socials
            </Text>
            <Link
              href="/article/manage?tab=profile"
              fontWeight={900}
              fontSize="sm"
              color={'growth-green'}
              target="_blank"
              display={'flex'}
              gap={2}
              whiteSpace={'nowrap'}
              alignItems={'center'}
            >
              Update profile
              <Image display="inline" src={ArrowUpLightGreen} />
            </Link>
          </Box>
        </Box>
        <Divider my="1.25rem" color="anti-flash-white" opacity={1} />
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <Box
              display="flex"
              alignItems={{ base: 'flex-start', md: 'center' }}
              justifyContent="space-between"
              maxW={'100%'}
              flexDirection={{ base: 'column', md: 'row' }}
              gap={{ base: '0.75rem', md: '0rem' }}
            >
              <Text
                fontWeight={600}
                color="jet-black"
                w={{ base: '100%', md: '35%' }}
                fontSize={'sm'}
              >
                Username{' '}
                <Text color="grey" as="span" fontStyle="italic">
                  (required)
                </Text>
              </Text>
              <Box w={{ base: '100%', md: '65%' }} display="flex" gap="1.5rem">
                <Box display="flex" w="100%" flexDirection="column">
                  <InputHookForm
                    bg="light.100"
                    p={{ base: '8px 12px', md: '10px 14px' }}
                    rounded="8px"
                    name="username"
                    placeholder="Enter Username"
                  ></InputHookForm>
                </Box>
                <FormErrorMessage>{errors.username && errors.username.message}</FormErrorMessage>
              </Box>
            </Box>
            <Divider my="1.25rem" color="anti-flash-white" opacity={1} />
            <Box
              display="flex"
              alignItems={{ base: 'flex-start', md: 'center' }}
              justifyContent="space-between"
              maxW={'100%'}
              flexDirection={{ base: 'column', md: 'row' }}
              gap={{ base: '0.75rem', md: '0rem' }}
            >
              <Text
                fontWeight={600}
                color="jet-black"
                w={{ base: '100%', md: '35%' }}
                fontSize={'sm'}
              >
                Email address
              </Text>
              <Box
                w={{ base: '100%', md: '65%' }}
                display="flex"
                gap={{ base: '0.75rem', md: '1.5rem' }}
                flexDirection={{ base: 'column', md: 'row' }}
              >
                <InputGroup>
                  <InputLeftElement h={{ base: '38px', md: '42px' }} w="3rem">
                    <Image
                      w="1rem"
                      src={!!profileData?.email ? EmailDisableIcon : EmailIcon}
                      alt=""
                    />
                  </InputLeftElement>
                  <InputHookForm
                    type="text"
                    bg="light.100"
                    py={{ base: '8px', md: '10px' }}
                    pl="2.625rem"
                    rounded="8px"
                    placeholder="Enter your email"
                    name="email"
                    isReadOnly={!!profileData?.email}
                    _readOnly={{
                      _focusVisible: {
                        borderColor: 'inherit'
                      },
                      color: 'mid-gray'
                    }}
                  ></InputHookForm>
                </InputGroup>
              </Box>
            </Box>
            <Divider my="1.25rem" color="anti-flash-white" opacity={1} />
            <Box
              display="flex"
              alignItems={{ base: 'flex-start', md: 'center' }}
              justifyContent="space-between"
              maxW={'100%'}
              flexDirection={{ base: 'column', md: 'row' }}
              gap={{ base: '0.75rem', md: '0rem' }}
            >
              <Text
                fontWeight={600}
                color="jet-black"
                w={{ base: '100%', md: '35%' }}
                fontSize={'sm'}
              >
                Recovery email
              </Text>
              <Box
                w={{ base: '100%', md: '65%' }}
                display="flex"
                gap={{ base: '0.75rem', md: '1.5rem' }}
                flexDirection={{ base: 'column', md: 'row' }}
              >
                <InputGroup>
                  <InputLeftElement h={{ base: '38px', md: '42px' }} w="3rem">
                    <Image
                      w="1rem"
                      src={!!profileData?.recoveryEmail ? EmailDisableIcon : EmailIcon}
                      alt=""
                    />
                  </InputLeftElement>
                  <InputHookForm
                    type="text"
                    bg="light.100"
                    py={{ base: '8px', md: '10px' }}
                    pl="2.625rem"
                    pr={!recoveryEmail ? '24px' : { base: '100px', md: '6.625rem' }}
                    rounded="8px"
                    placeholder="Enter your recovery email"
                    name="recoveryEmail"
                    isReadOnly={!!profileData?.recoveryEmail}
                    _readOnly={{
                      _focusVisible: {
                        borderColor: 'inherit'
                      },
                      color: 'mid-gray'
                    }}
                  ></InputHookForm>
                  {!profileData?.recoveryEmail && recoveryEmail && (
                    <InputRightElement
                      h={{ base: '38px', md: '42px' }}
                      w="5.188"
                      mr="0.875rem"
                      cursor={'pointer'}
                      onClick={handleVerifyRecoveryEmail}
                    >
                      <Text fontWeight={900} fontSize="sm" color={'growth-green'}>
                        Verify Email
                      </Text>
                    </InputRightElement>
                  )}
                </InputGroup>
              </Box>
            </Box>
            <Divider my="1.25rem" color="anti-flash-white" opacity={1} />
            <Box
              display="flex"
              alignItems={{ base: 'flex-start', md: 'center' }}
              justifyContent="space-between"
              maxW={'100%'}
              flexDirection={{ base: 'column', md: 'row' }}
              gap={{ base: '0.75rem', md: '0rem' }}
            >
              <Text
                fontWeight={600}
                color="jet-black"
                w={{ base: '100%', md: '35%' }}
                fontSize={'sm'}
              >
                Birthday
              </Text>
              <Box
                w={{ base: '100%', md: '65%' }}
                display="flex"
                gap={{ base: '0.75rem', md: '1.5rem' }}
                flexDirection={{ base: 'column', md: 'row' }}
              >
                <InputGroup>
                  <InputLeftElement h={{ base: '38px', md: '42px' }} w="3rem">
                    <Image
                      w="1rem"
                      src={!!profileData?.birthday ? CalendarDisableIcon : CalendarIcon}
                      alt=""
                    />
                  </InputLeftElement>
                  <InputHookForm
                    type="text"
                    bg="light.100"
                    py={{ base: '8px', md: '10px' }}
                    pl="2.625rem"
                    rounded="8px"
                    placeholder="MM/DD/YYYY"
                    name="birthday"
                    isReadOnly={!!profileData?.birthday}
                    _readOnly={{
                      _focusVisible: {
                        borderColor: 'inherit'
                      },
                      color: 'mid-gray'
                    }}
                  ></InputHookForm>
                </InputGroup>
              </Box>
            </Box>
            <Divider my="1.25rem" color="anti-flash-white" opacity={1} />
            <Box
              display="flex"
              alignItems={{ base: 'flex-start', md: 'center' }}
              justifyContent="space-between"
              maxW={'100%'}
              flexDirection={{ base: 'column', md: 'row' }}
              gap={{ base: '0.75rem', md: '0rem' }}
            >
              <Text
                fontWeight={600}
                color="jet-black"
                w={{ base: '100%', md: '35%' }}
                fontSize={'sm'}
              >
                Gender
              </Text>
              <Box
                w={{ base: '100%', md: '65%' }}
                display="flex"
                gap={{ base: '0.75rem', md: '1.5rem' }}
                flexDirection={{ base: 'column', md: 'row' }}
              >
                <Menu matchWidth={true}>
                  <MenuButton
                    as={Button}
                    rightIcon={<Image src={ArrowDownIconSolid} alt="" />}
                    height={{ base: '38px', md: '42px' }}
                    p={{ base: '8px 12px', md: '10px 14px' }}
                    w={'full'}
                    border={'1px solid'}
                    borderColor={'anti-flash-white'}
                    bg="light.100"
                    boxShadow={'none'}
                    rounded="8px"
                  >
                    <Box display={'flex'} alignItems={'center'} columnGap={'8px'}>
                      <Image src={gender?.icon} alt="" w="1rem" />
                      <Text fontSize={'md'} fontWeight={400} color={'jet-black'}>
                        {gender?.value}
                      </Text>
                    </Box>
                  </MenuButton>
                  <MenuList
                    p={'8px'}
                    minW={'unset'}
                    borderRadius={'12px'}
                    bg={'white'}
                    boxShadow={
                      '0px 4px 6px rgba(16, 24, 40, 0.03), 0px 12px 16px rgba(16, 24, 40, 0.08)'
                    }
                    border={'1px solid'}
                    borderColor={'anti-flash-lite'}
                  >
                    {GENDER.map((item: any, index: any) => {
                      return (
                        <MenuItem
                          key={index}
                          p={'8px 10px'}
                          borderRadius={'8px'}
                          onClick={() => handleSelectGender(item)}
                          _focus={{ bgColor: 'white' }}
                          _hover={{
                            bgColor: 'anti-flash-white'
                          }}
                        >
                          <Box display={'flex'} alignItems={'center'} columnGap={'8px'}>
                            <Image src={item.icon} w="1rem" alt="" />
                            <Text fontSize={'sm'} fontWeight={800} color={'jet-black'}>
                              {item.value}
                            </Text>
                          </Box>
                          {item.name}
                        </MenuItem>
                      );
                    })}
                  </MenuList>
                </Menu>
              </Box>
            </Box>
            <Divider my="1.25rem" color="anti-flash-white" opacity={1} />
            <Box display="flex" gap="0.75rem" justifyContent={'flex-end'} alignContent={'center'}>
              <TwygsButton onClick={() => methods.reset()} className="h-10" variant="outline">
                Cancel
              </TwygsButton>
              <TwygsButton isLoading={isLoading} className="h-10" type="submit" variant="primary">
                Update
              </TwygsButton>
            </Box>
          </form>
        </FormProvider>
      </Box>
      {renderModal()}
    </Box>
  );
};
